import { indexToRC } from './indexToRC'

/**
 * Checks if a tile can move into the empty space
 *
 * A tile can move if it's orthogonally adjacent to the empty cell
 * (Manhattan distance === 1). Diagonal moves are not allowed.
 *
 * @param {number[]} boardArray - Current board state
 * @param {number} index - Index of the tile to check
 * @param {number} size - Board dimension (e.g., 3 for 3x3)
 * @param {number} emptyValue - Value representing the empty cell (typically size*size - 1)
 * @returns {boolean} True if tile can move, false otherwise
 *
 * @example
 * // For a 3x3 board: [0, 1, 2, 3, 8, 5, 6, 7, 4]
 * // Visual:
 * // [0][1][2]
 * // [3][X][5]  <- empty (8) is at index 4
 * // [6][7][4]
 * canMoveIn([0,1,2,3,8,5,6,7,4], 1, 3, 8)  // true (above empty)
 * canMoveIn([0,1,2,3,8,5,6,7,4], 3, 3, 8)  // true (left of empty)
 * canMoveIn([0,1,2,3,8,5,6,7,4], 0, 3, 8)  // false (diagonal)
 * canMoveIn([0,1,2,3,8,5,6,7,4], 4, 3, 8)  // false (is the empty cell)
 *
 * @example
 * // Out of bounds handling
 * canMoveIn([0,1,2,3], -1, 2, 3)  // false
 * canMoveIn([0,1,2,3], 10, 2, 3)  // false
 */
export function canMoveIn (boardArray, index, size, emptyValue) {
  if (index < 0 || index >= boardArray.length) return false
  const emptyIdx = boardArray.indexOf(emptyValue)
  const [ir, ic] = indexToRC(index, size)
  const [er, ec] = indexToRC(emptyIdx, size)
  return Math.abs(ir - er) + Math.abs(ic - ec) === 1
}
